<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Input from 'next/input/Input.vue';
import Spinner from 'next/spinner/Spinner.vue';
import PaginationFooter from 'next/pagination/PaginationFooter.vue';
import PostGrid from './components/PostGrid.vue';
import FilterMenu from './components/FilterMenu.vue';
import SortMenu from './components/SortMenu.vue';
import NewPostDialog from './components/NewPostDialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const posts = useMapGetter('wgptSocialMediaPosts/getSocialMediaPosts');
const meta = useMapGetter('wgptSocialMediaPosts/getMeta');
const uiFlags = useMapGetter('wgptSocialMediaPosts/getUIFlags');
const getInbox = useMapGetter('inboxes/getInbox');
const { isAdmin } = useAdmin();

const searchQuery = ref(route.query.q || '');
const sortOrder = ref(route.query.sort_order || 'desc');
const statusValues = ref(
  route.query.status ? [].concat(route.query.status) : []
);
const statusNotValues = ref(
  route.query.status_not ? [].concat(route.query.status_not) : []
);

const showFilterPopup = ref(false);

// One popup row per active condition - status and status_not can both be applied at once.
const buildStatusFilter = (filterOperator, values) => ({
  attributeKey: 'status',
  filterOperator,
  values: values.map(id => ({ id, name: id })),
  queryOperator: 'or',
});

const seededFilters = [
  ...(statusValues.value.length
    ? [buildStatusFilter('equal_to', statusValues.value)]
    : []),
  ...(statusNotValues.value.length
    ? [buildStatusFilter('not_equal_to', statusNotValues.value)]
    : []),
];
const appliedFilters = ref(
  seededFilters.length ? seededFilters : [buildStatusFilter('equal_to', [])]
);

const hasAppliedFilters = computed(
  () => statusValues.value.length > 0 || statusNotValues.value.length > 0
);

const inboxId = computed(() => route.params.inboxId);
const currentPage = computed(() => Number(meta.value.page) || 1);
const totalItems = computed(() => meta.value.total || 0);
const isFetching = computed(() => uiFlags.value.isFetching);
const isCreating = computed(() => uiFlags.value.isCreating);
const hasRecords = computed(() => posts.value.length > 0);

const newPostDialogRef = ref(null);

const pageTitle = computed(() => {
  if (!inboxId.value) return t('WGPT_SOCIAL_MEDIA_POSTS.SIDEBAR.TITLE');
  return (
    getInbox.value(inboxId.value)?.name ||
    t('WGPT_SOCIAL_MEDIA_POSTS.SIDEBAR.TITLE')
  );
});

const fetchPosts = (page = 1) => {
  store.dispatch('wgptSocialMediaPosts/get', {
    page,
    q: searchQuery.value || undefined,
    status: statusValues.value.length ? statusValues.value : undefined,
    statusNot: statusNotValues.value.length ? statusNotValues.value : undefined,
    sortOrder: sortOrder.value,
    inboxId: inboxId.value,
  });
  router.replace({
    query: {
      ...route.query,
      page: page.toString(),
      q: searchQuery.value || undefined,
      status: statusValues.value.length ? statusValues.value : undefined,
      status_not: statusNotValues.value.length
        ? statusNotValues.value
        : undefined,
      sort_order: sortOrder.value,
    },
  });
};

const handlePageChange = page => fetchPosts(page);

const handleCreatePost = async payload => {
  try {
    await store.dispatch('wgptSocialMediaPosts/create', payload);
    newPostDialogRef.value?.onSuccess();
    useAlert(t('WGPT_SOCIAL_MEDIA_POSTS.NEW.MESSAGES.SUCCESS'));
    fetchPosts(1);
  } catch (error) {
    useAlert(error.message);
  }
};

const handleSearch = useDebounceFn(() => fetchPosts(1), 300);

const toggleFilterPopup = () => {
  showFilterPopup.value = !showFilterPopup.value;
};

const handleApplyFilter = filters => {
  const includedFilters = filters.filter(
    filter => filter.filterOperator !== 'not_equal_to'
  );
  const excludedFilters = filters.filter(
    filter => filter.filterOperator === 'not_equal_to'
  );

  statusValues.value = includedFilters.flatMap(filter =>
    (filter.values || []).map(value => value.id)
  );
  statusNotValues.value = excludedFilters.flatMap(filter =>
    (filter.values || []).map(value => value.id)
  );

  showFilterPopup.value = false;
  fetchPosts(1);
};

const handleSortChange = ({ order }) => {
  sortOrder.value = order;
  fetchPosts(1);
};

watch(inboxId, () => fetchPosts(1));

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchPosts(Number(route.query.page) || 1);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="px-6 py-6">
      <div
        class="flex items-center justify-between w-full gap-2 mx-auto max-w-5xl"
      >
        <span class="text-xl font-medium truncate text-n-slate-12">
          {{ pageTitle }}
        </span>
        <div class="flex items-center flex-shrink-0 gap-4">
          <div class="flex items-center gap-2">
            <Input
              v-model="searchQuery"
              type="search"
              :placeholder="
                t('WGPT_SOCIAL_MEDIA_POSTS.GRID.SEARCH_PLACEHOLDER')
              "
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              class="!mb-0 w-full max-w-[12.5rem]"
              @input="handleSearch"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
            <div class="relative">
              <Button
                id="toggleSocialMediaPostsFilterButton"
                icon="i-lucide-list-filter"
                color="slate"
                size="sm"
                variant="ghost"
                class="relative w-8"
                @click="toggleFilterPopup"
              >
                <div
                  v-if="hasAppliedFilters"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <FilterMenu
                v-if="showFilterPopup"
                v-model="appliedFilters"
                class="absolute mt-1 ltr:right-0 rtl:left-0 top-full"
                @apply-filter="handleApplyFilter"
                @close="showFilterPopup = false"
              />
            </div>
            <SortMenu
              :active-ordering="sortOrder"
              @update:sort="handleSortChange"
            />
          </div>
          <div class="w-px h-4 bg-n-strong" />
          <Button
            v-if="isAdmin"
            :label="t('WGPT_SOCIAL_MEDIA_POSTS.NEW.BUTTON')"
            icon="i-lucide-plus"
            size="sm"
            @click="newPostDialogRef?.open()"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto">
        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <div
          v-else-if="!hasRecords"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          {{ t('WGPT_SOCIAL_MEDIA_POSTS.GRID.EMPTY_STATE.TITLE') }}
        </div>
        <PostGrid v-else :posts="posts" />
      </div>
    </main>

    <footer v-if="!isFetching && hasRecords" class="sticky bottom-0 z-0 px-6">
      <PaginationFooter
        current-page-info="WGPT_SOCIAL_MEDIA_POSTS.PAGINATION_FOOTER.SHOWING"
        :current-page="currentPage"
        :total-items="totalItems"
        class="max-w-5xl mx-auto"
        :items-per-page="meta.size || 25"
        @update:current-page="handlePageChange"
      />
    </footer>

    <NewPostDialog
      ref="newPostDialogRef"
      :is-loading="isCreating"
      @create="handleCreatePost"
    />
  </section>
</template>
